var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import classNames from 'classnames';
import useTaroBaseEvents from '../_util/hooks/useTaroBaseEvents';
import useMergedState from '../_util/hooks/useMergedState';
import { createTaroFocusEvent, createTaroBlurEvent, createTaroConfirmEvent } from '../_util/taroEvent';
import useField from '../form/useField';
const Input = (_a, ref) => {
    var { className, name, value, type = 'text', password = false, placeholder, placeholderStyle: customPlaceholderStyle, placeholderClass = 'input-placeholder', disabled = false, maxlength = 140, focus, confirmHold = false, cursor, selectionStart = -1, selectionEnd = -1, onInput, onFocus, onBlur, onConfirm, 
    // unsupported props
    confirmType, adjustPosition, holdKeyboard, alwaysEmbed, safePasswordCertPath, safePasswordLength, safePasswordTimeStamp, safePasswordNonce, safePasswordSalt, safePasswordCustomHash, randomNumber, controlled } = _a, rest = __rest(_a, ["className", "name", "value", "type", "password", "placeholder", "placeholderStyle", "placeholderClass", "disabled", "maxlength", "focus", "confirmHold", "cursor", "selectionStart", "selectionEnd", "onInput", "onFocus", "onBlur", "onConfirm", "confirmType", "adjustPosition", "holdKeyboard", "alwaysEmbed", "safePasswordCertPath", "safePasswordLength", "safePasswordTimeStamp", "safePasswordNonce", "safePasswordSalt", "safePasswordCustomHash", "randomNumber", "controlled"]);
    const props = useTaroBaseEvents(rest);
    const inputEl = useRef(null);
    const placeholderEl = useRef(null);
    const keyCode = useRef(null);
    useImperativeHandle(ref, () => inputEl.current);
    const [mergedValue, setMergedValue] = useMergedState('', {
        value
    });
    useField(name, mergedValue);
    useEffect(() => {
        if (!placeholderEl.current) {
            return;
        }
        if (typeof customPlaceholderStyle === 'string') {
            placeholderEl.current.setAttribute('style', customPlaceholderStyle);
        }
        else {
            placeholderEl.current.setAttribute('style', '');
        }
    }, [customPlaceholderStyle]);
    useEffect(() => {
        if (!inputEl.current) {
            return;
        }
        if (focus) {
            inputEl.current.focus();
        }
        if (typeof cursor === 'number') {
            inputEl.current.setSelectionRange(cursor, cursor);
        }
        else if (typeof selectionStart === 'number' &&
            selectionStart !== -1 &&
            typeof selectionEnd === 'number' &&
            selectionEnd !== -1) {
            inputEl.current.setSelectionRange(selectionStart, selectionEnd);
        }
    }, []);
    const placeholderStyle = Object.assign({
        display: mergedValue ? 'none' : 'block'
    }, typeof customPlaceholderStyle === 'object'
        ? customPlaceholderStyle
        : undefined);
    return (_jsx("div", Object.assign({ className: classNames('taro-input', className) }, props, { children: _jsxs("div", Object.assign({ className: 'taro-input_content' }, { children: [_jsx("div", Object.assign({ ref: placeholderEl, className: classNames('taro-input_placeholder', placeholderClass), style: placeholderStyle }, { children: placeholder })), _jsx("input", { ref: inputEl, className: 'taro-input_main', name: name, value: mergedValue, type: password ? 'password' : type, disabled: disabled, maxLength: maxlength === -1 ? undefined : maxlength, onChange: event => {
                        const el = event.target;
                        if (onInput) {
                            const { timeStamp, target, currentTarget } = event;
                            const el = event.target;
                            const taroEvent = {
                                currentTarget,
                                target,
                                detail: {
                                    cursor: el.selectionStart || 0,
                                    keyCode: keyCode.current,
                                    value: el.value
                                },
                                timeStamp,
                                type: 'input',
                                preventDefault: () => event.preventDefault(),
                                stopPropagation: () => event.stopPropagation()
                            };
                            onInput(taroEvent);
                        }
                        setMergedValue(el.value);
                    }, onFocus: event => {
                        if (onFocus) {
                            const taroEvent = createTaroFocusEvent(event);
                            onFocus(taroEvent);
                        }
                    }, onBlur: event => {
                        if (onBlur) {
                            const taroEvent = createTaroBlurEvent(event);
                            onBlur(taroEvent);
                        }
                    }, onKeyDown: event => {
                        keyCode.current = event.keyCode;
                        if (onConfirm && 'Enter' === event.key) {
                            const el = event.target;
                            if (!confirmHold) {
                                el.blur();
                            }
                            const taroEvent = createTaroConfirmEvent(event);
                            onConfirm(taroEvent);
                        }
                    } })] })) })));
};
export default forwardRef(Input);
